import { inject, Injectable } from '@angular/core';
import { Router, CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { AuthService } from './auth.service';

/** Decode the ?data= proxy-login param and store token/user in sessionStorage.
 *  Returns true if a valid token was found and stored. */
function applyProxyLoginParam(route: ActivatedRouteSnapshot, authService: AuthService): boolean {
  const dataParam = route.queryParamMap.get('data');
  if (!dataParam) return false;
  try {
    const decoded = JSON.parse(atob(dataParam));
    if (decoded?.token) {
      authService.setToken(decoded.token);
    }
    if (decoded?.user) {
      sessionStorage.setItem('userData', JSON.stringify(decoded.user));
    }
    return !!decoded?.token;
  } catch {
    return false;
  }
}

@Injectable({
  providedIn: 'root'
})
export class AuthGuardService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): boolean {
    if (this.authService.isAuthenticated()) {
      return true;
    }

    // Redirect to login if not authenticated
    this.router.navigate(['/auth/login']);
    return false;
  }
}


export const authGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (authService.isAuthenticated()) {
    return true;
  }

  router.navigate(['/auth/login']);
  return false;
};

// Public route guard (redirect to dashboard if already authenticated)
export const publicGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Setup session (no real token) → clear and allow login
  if (authService.isSetupSession()) {
    sessionStorage.clear();
    return true;
  }

  if (authService.isAuthenticated()) {
    const userData = authService.userData;
    const setupComplete =
      userData?.isTraceabilitySetup == 1 &&
      userData?.isTermsCondition == 1
    // Incomplete profile setup → clear session and allow login
    if (!setupComplete) {
      sessionStorage.clear();
      return true;
    }

    router.navigate(['/dashboard']);
    return false;
  }

  return true;
};

// Profile-setup guard: allow UUID links always; without UUID only during setup session
export const profileSetupGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // UUID link — always allow (both logged-in and logged-out users)
  if (route.paramMap.get('uuid')) {
    return true;
  }

  if (authService.isSetupSession()) {
    return true;
  }

  // Normal authenticated user hitting /profile-setup without UUID → dashboard
  if (authService.isAuthenticated()) {
    router.navigate(['/dashboard']);
    return false;
  }

  // Not authenticated → login
  router.navigate(['/auth/login']);
  return false;
};

// authGuard extended: block access to other modules during setup session
export const setupBlockGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  applyProxyLoginParam(route, authService);

  if (authService.isSetupSession()) {
    router.navigate(['/profile-setup']);
    return false;
  }

  if (authService.isAuthenticated()) {
    const userData = authService.userData;
    const setupComplete =
      userData?.isTraceabilitySetup == 1 &&
      userData?.isTermsCondition == 1
    if (!setupComplete && userData?.uuid) {
      router.navigate(['/profile-setup', userData.uuid]);
      return false;
    }

    return true;
  }

  router.navigate(['/auth/login']);
  return false;
};
